import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider } from "@/lib/i18n";
import { AuthProvider } from "@/lib/AuthContext";
import { AccountProvider } from "@/lib/AccountContext";
import { SettingsProvider } from "@/lib/SettingsContext";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import ProtectedAdminRoute from "@/components/ProtectedAdminRoute";
import ProtectedAccountRoute from "@/components/ProtectedAccountRoute";
import UrlLanguageSync from "@/components/UrlLanguageSync";

import Home from "@/pages/Home";
import Catalogue from "@/pages/Catalogue";
import InstrumentDetail from "@/pages/InstrumentDetail";
import Consent from "@/pages/Consent";
import Demographics from "@/pages/Demographics";
import Questionnaire from "@/pages/Questionnaire";
import Results from "@/pages/Results";
import Share from "@/pages/Share";
import Feedback from "@/pages/Feedback";
import AdminFeedback from "@/pages/admin/Feedback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import LegalPage from "@/pages/LegalPage";
import NotFound from "@/pages/NotFound";
import LoginRedirect from "@/components/LoginRedirect";
import AccountAuth from "@/pages/AccountAuth";
import MyResults from "@/pages/MyResults";
import AccountForgotPassword from "@/pages/AccountForgotPassword";
import AccountResetPassword from "@/pages/AccountResetPassword";
import AccountVerifyEmail from "@/pages/AccountVerifyEmail";
import AccountSettings from "@/pages/AccountSettings";

import AdminDashboard from "@/pages/admin/Dashboard";
import AdminInstruments from "@/pages/admin/Instruments";
import InstrumentBuilder from "@/pages/admin/InstrumentBuilder";
import AdminCategories from "@/pages/admin/Categories";
import AdminResponses from "@/pages/admin/Responses";
import AdminSettings from "@/pages/admin/Settings";
import AdminUsers from "@/pages/admin/Users";
import AdminPublicUsers from "@/pages/admin/PublicUsers";
import AdminAudit from "@/pages/admin/Audit";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminLegalPages from "@/pages/admin/LegalPages";
import AdminFaq from "@/pages/admin/Faq";
import Admin2FA from "@/pages/admin/TwoFactor";
import AdminMaintenance from "@/pages/admin/Maintenance";

function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <SettingsProvider>
        <BrowserRouter>
          <AccountProvider>
          <UrlLanguageSync />
          <div className="min-h-screen bg-background text-foreground flex flex-col">
            <PublicNav />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/instruments" element={<Catalogue />} />
                <Route path="/instruments/:slug" element={<InstrumentDetail />} />
                <Route path="/en" element={<Home />} />
                <Route path="/en/instruments" element={<Catalogue />} />
                <Route path="/en/instruments/:slug" element={<InstrumentDetail />} />
                <Route path="/instruments/:slug/consent" element={<Consent />} />
                <Route path="/instruments/:slug/demographics" element={<Demographics />} />
                <Route path="/instruments/:slug/questionnaire" element={<Questionnaire />} />
                <Route path="/results/:code" element={<Results />} />
                <Route path="/share/:token" element={<Share />} />
                <Route path="/feedback" element={<Feedback />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/legal/:slug" element={<LegalPage />} />
                <Route path="/login" element={<AccountAuth mode="login" />} />
                <Route path="/account/login" element={<LoginRedirect />} />
                <Route path="/account/register" element={<AccountAuth mode="register" />} />
                <Route path="/account/forgot-password" element={<AccountForgotPassword />} />
                <Route path="/account/reset-password" element={<AccountResetPassword />} />
                <Route path="/account/verify-email" element={<AccountVerifyEmail />} />
                <Route path="/account/results" element={<ProtectedAccountRoute><MyResults /></ProtectedAccountRoute>} />
                <Route path="/account/settings" element={<ProtectedAccountRoute><AccountSettings /></ProtectedAccountRoute>} />

                <Route path="/admin/login" element={<LoginRedirect />} />
                <Route path="/admin" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
                <Route path="/admin/instruments" element={<ProtectedAdminRoute><AdminInstruments /></ProtectedAdminRoute>} />
                <Route path="/admin/instruments/new" element={<ProtectedAdminRoute><InstrumentBuilder /></ProtectedAdminRoute>} />
                <Route path="/admin/instruments/:id/edit" element={<ProtectedAdminRoute><InstrumentBuilder /></ProtectedAdminRoute>} />
                <Route path="/admin/categories" element={<ProtectedAdminRoute><AdminCategories /></ProtectedAdminRoute>} />
                <Route path="/admin/responses" element={<ProtectedAdminRoute><AdminResponses /></ProtectedAdminRoute>} />
                <Route path="/admin/analytics" element={<ProtectedAdminRoute><AdminAnalytics /></ProtectedAdminRoute>} />
                <Route path="/admin/public-users" element={<ProtectedAdminRoute allowedRoles={["admin"]}><AdminPublicUsers /></ProtectedAdminRoute>} />
                <Route path="/admin/users" element={<ProtectedAdminRoute><AdminUsers /></ProtectedAdminRoute>} />
                <Route path="/admin/audit" element={<ProtectedAdminRoute><AdminAudit /></ProtectedAdminRoute>} />
                <Route path="/admin/feedback" element={<ProtectedAdminRoute><AdminFeedback /></ProtectedAdminRoute>} />
                <Route path="/admin/2fa" element={<ProtectedAdminRoute><Admin2FA /></ProtectedAdminRoute>} />
                <Route path="/admin/settings" element={<ProtectedAdminRoute><AdminSettings /></ProtectedAdminRoute>} />
                <Route path="/admin/legal" element={<ProtectedAdminRoute><AdminLegalPages /></ProtectedAdminRoute>} />
                <Route path="/admin/faq" element={<ProtectedAdminRoute><AdminFaq /></ProtectedAdminRoute>} />
                <Route path="/admin/maintenance" element={<ProtectedAdminRoute><AdminMaintenance /></ProtectedAdminRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
          </div>
          <Toaster position="bottom-right" richColors />
          </AccountProvider>
        </BrowserRouter>
        </SettingsProvider>
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
